import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FileUploadDropzone } from "@/components/ui/file-upload-dropzone";
import { toast } from "sonner";
import { Loader2, Check, AlertTriangle, Euro, User } from "lucide-react";

interface Employee {
  id: string;
  name: string;
  employee_number?: string | null;
}

interface SalaryEntry {
  description: string;
  amount: number;
  effective_month: string | null;
  effective_year: string | null;
  notes: string | null;
}

interface EmployeeBlock {
  employee_name: string | null;
  employee_number: string | null;
  salary_entries: SalaryEntry[];
  document_date: string | null;
  additional_info: string | null;
  // UI state
  matchedEmployee: Employee | null;
  manualEmployeeId: string;
  selectedEntries: Set<number>;
}

export function EmployeeMasterDataPdfImport() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [employeeBlocks, setEmployeeBlocks] = useState<EmployeeBlock[]>([]);

  useEffect(() => {
    if (user) {
      loadEmployees();
    }
  }, [user]);

  const loadEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from("employees")
        .select("id, name, employee_number")
        .is("archived_at", null)
        .order("name");

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error("Error loading employees:", error);
      toast.error("Fehler beim Laden der Mitarbeiter");
    } finally {
      setLoading(false);
    }
  };

  const findMatchingEmployee = useCallback((name: string | null, empNumber: string | null): Employee | null => {
    if (empNumber) {
      const match = employees.find(e =>
        e.employee_number?.toLowerCase() === empNumber.toLowerCase()
      );
      if (match) return match;
    }

    if (name) {
      const normalizedName = name.toLowerCase().trim();
      const match = employees.find(e => {
        const empName = e.name.toLowerCase().trim();
        return empName === normalizedName ||
          empName.includes(normalizedName) ||
          normalizedName.includes(empName);
      });
      if (match) return match;
    }

    return null;
  }, [employees]);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setEmployeeBlocks([]);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Bitte wählen Sie eine Datei aus");
      return;
    }

    setUploading(true);
    setEmployeeBlocks([]);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Nicht angemeldet");
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/parse-masterdata-salary`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Fehler beim Analysieren der Datei");
      }

      const data = result.data;
      let blocks: EmployeeBlock[];

      if (data.multi_employee && Array.isArray(data.employees)) {
        // Multi-employee PDF
        blocks = data.employees.map((emp: any) => {
          const matched = findMatchingEmployee(emp.employee_name, emp.employee_number);
          return {
            employee_name: emp.employee_name,
            employee_number: emp.employee_number,
            salary_entries: emp.salary_entries || [],
            document_date: emp.document_date,
            additional_info: emp.additional_info,
            matchedEmployee: matched,
            manualEmployeeId: matched?.id || "",
            selectedEntries: new Set<number>((emp.salary_entries || []).map((_: any, i: number) => i)),
          };
        });
      } else {
        // Single employee PDF (backwards compatible)
        const matched = findMatchingEmployee(data.employee_name, data.employee_number);
        blocks = [{
          employee_name: data.employee_name,
          employee_number: data.employee_number,
          salary_entries: data.salary_entries || [],
          document_date: data.document_date,
          additional_info: data.additional_info,
          matchedEmployee: matched,
          manualEmployeeId: matched?.id || "",
          selectedEntries: new Set<number>((data.salary_entries || []).map((_: any, i: number) => i)),
        }];
      }

      setEmployeeBlocks(blocks);
      setDialogOpen(true);
      toast.success(`${blocks.length} Mitarbeiter-Abschnitt(e) erkannt`);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error instanceof Error ? error.message : "Fehler beim Hochladen");
    } finally {
      setUploading(false);
    }
  };

  const updateBlock = (index: number, updates: Partial<EmployeeBlock>) => {
    setEmployeeBlocks(prev => prev.map((b, i) => i === index ? { ...b, ...updates } : b));
  };

  const toggleEntry = (blockIndex: number, entryIndex: number) => {
    setEmployeeBlocks(prev => prev.map((block, bi) => {
      if (bi !== blockIndex) return block;
      const newSelected = new Set(block.selectedEntries);
      if (newSelected.has(entryIndex)) {
        newSelected.delete(entryIndex);
      } else {
        newSelected.add(entryIndex);
      }
      return { ...block, selectedEntries: newSelected };
    }));
  };

  const handleSave = async () => {
    const blocksToSave = employeeBlocks.filter(b => b.manualEmployeeId && b.selectedEntries.size > 0);

    if (blocksToSave.length === 0) {
      toast.error("Bitte wählen Sie mindestens einen Mitarbeiter und Gehaltseintrag aus");
      return;
    }

    setSaving(true);
    let totalSaved = 0;

    try {
      for (const block of blocksToSave) {
        const entriesToSave = block.salary_entries.filter((_, index) =>
          block.selectedEntries.has(index)
        );

        for (const entry of entriesToSave) {
          if (!entry.amount || entry.amount <= 0) continue;

          let effectiveDate: string;
          if (entry.effective_year && entry.effective_month) {
            effectiveDate = `${entry.effective_year}-${entry.effective_month.padStart(2, '0')}-01`;
          } else if (entry.effective_year) {
            effectiveDate = `${entry.effective_year}-01-01`;
          } else {
            effectiveDate = new Date().toISOString().split('T')[0];
          }

          const { error } = await supabase
            .from("salary_history")
            .insert([{
              employee_id: block.manualEmployeeId,
              effective_date: effectiveDate,
              base_salary: entry.amount,
              currency: "EUR",
              salary_type: "monthly",
              payment_frequency: "monthly",
              reason: "masterdata_import",
              notes: `${entry.description}${entry.notes ? ` - ${entry.notes}` : ''} - Importiert aus Stammdaten-PDF`,
              created_by: user!.id,
            }]);

          if (error) throw error;
          totalSaved++;
        }
      }

      toast.success(`${totalSaved} Gehaltseintrag/einträge für ${blocksToSave.length} Mitarbeiter importiert`);
      setDialogOpen(false);
      setSelectedFile(null);
      setEmployeeBlocks([]);
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Fehler beim Speichern der Daten");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return "-";
    return value.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const MASTERDATA_IMPORT_FIELDS = [
    { label: "Mitarbeitername", required: false },
    { label: "Personalnummer", required: false },
    { label: "Festbezug / Gehalt", required: true },
    { label: "Gültig ab (Monat/Jahr)", required: true },
  ];

  const totalSelectedEntries = employeeBlocks.reduce((sum, b) => sum + b.selectedEntries.size, 0);
  const assignedBlocks = employeeBlocks.filter(b => b.manualEmployeeId).length;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Euro className="h-5 w-5" />
            Stammdaten Gehalt Import
          </CardTitle>
          <CardDescription>
            Laden Sie ein Stammdatenblatt (PDF) hoch – auch mit mehreren Mitarbeitern.
            Die KI erkennt automatisch Abschnitte wie "Personaldaten für ..." und extrahiert Gehaltsinformationen pro Mitarbeiter.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FileUploadDropzone
            accept=".pdf,image/*"
            onFileSelect={handleFileSelect}
            selectedFile={selectedFile}
            onClear={handleClearFile}
            title="Stammdatenblatt hochladen"
            description="Ziehen Sie eine PDF- oder Bilddatei hierher (auch mit mehreren Mitarbeitern)"
            acceptedFormats={["PDF", "PNG", "JPG", "JPEG"]}
            supportedFields={MASTERDATA_IMPORT_FIELDS}
            disabled={uploading}
          />

          {selectedFile && !uploading && (
            <div className="flex justify-center">
              <Button onClick={handleUpload}>
                Hochladen & Analysieren
              </Button>
            </div>
          )}

          {uploading && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-muted-foreground">KI analysiert das Stammdatenblatt...</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Extrahierte Gehaltsdaten</DialogTitle>
            <DialogDescription>
              {employeeBlocks.length > 1
                ? `${employeeBlocks.length} Mitarbeiter im Dokument erkannt. Weisen Sie die Daten den richtigen Mitarbeitern zu.`
                : "Überprüfen Sie die extrahierten Festbezüge und wählen Sie den Mitarbeiter aus."}
            </DialogDescription>
          </DialogHeader>

          {employeeBlocks.length > 0 && (
            <div className="space-y-2">
              {employeeBlocks.length > 1 ? (
                <Accordion type="multiple" defaultValue={employeeBlocks.map((_, i) => `block-${i}`)} className="space-y-2">
                  {employeeBlocks.map((block, blockIndex) => (
                    <AccordionItem key={blockIndex} value={`block-${blockIndex}`} className="border rounded-lg">
                      <AccordionTrigger className="px-4 hover:no-underline">
                        <div className="flex items-center gap-3 w-full">
                          <User className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="font-medium">{block.employee_name || `Mitarbeiter ${blockIndex + 1}`}</span>
                          {block.employee_number && (
                            <Badge variant="secondary" className="text-xs">Nr. {block.employee_number}</Badge>
                          )}
                          <div className="ml-auto flex items-center gap-2 mr-2">
                            {block.manualEmployeeId ? (
                              <Badge variant="default" className="bg-primary text-xs">
                                <Check className="mr-1 h-3 w-3" />
                                Zugeordnet
                              </Badge>
                            ) : (
                              <Badge variant="destructive" className="text-xs">
                                <AlertTriangle className="mr-1 h-3 w-3" />
                                Nicht zugeordnet
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-xs">
                              {block.selectedEntries.size} Einträge
                            </Badge>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <EmployeeBlockContent
                          block={block}
                          blockIndex={blockIndex}
                          employees={employees}
                          onUpdateBlock={updateBlock}
                          onToggleEntry={toggleEntry}
                          formatCurrency={formatCurrency}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <EmployeeBlockContent
                  block={employeeBlocks[0]}
                  blockIndex={0}
                  employees={employees}
                  onUpdateBlock={updateBlock}
                  onToggleEntry={toggleEntry}
                  formatCurrency={formatCurrency}
                />
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || assignedBlocks === 0 || totalSelectedEntries === 0}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {totalSelectedEntries} Einträge für {assignedBlocks} Mitarbeiter importieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface EmployeeBlockContentProps {
  block: EmployeeBlock;
  blockIndex: number;
  employees: Employee[];
  onUpdateBlock: (index: number, updates: Partial<EmployeeBlock>) => void;
  onToggleEntry: (blockIndex: number, entryIndex: number) => void;
  formatCurrency: (value: number | null | undefined) => string;
}

function EmployeeBlockContent({ block, blockIndex, employees, onUpdateBlock, onToggleEntry, formatCurrency }: EmployeeBlockContentProps) {
  return (
    <div className="space-y-4">
      {/* Employee Matching */}
      <div className="p-4 rounded-lg border bg-muted/50">
        <div className="flex items-center gap-2 mb-3">
          {block.matchedEmployee ? (
            <Badge variant="default" className="bg-primary">
              <Check className="mr-1 h-3 w-3" />
              Automatisch zugeordnet
            </Badge>
          ) : (
            <Badge variant="destructive">
              <AlertTriangle className="mr-1 h-3 w-3" />
              Bitte manuell zuordnen
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-muted-foreground">Aus Dokument</Label>
            <p className="font-medium">{block.employee_name || "-"}</p>
            {block.employee_number && (
              <p className="text-sm text-muted-foreground">
                Personalnr.: {block.employee_number}
              </p>
            )}
          </div>
          <div>
            <Label>Zuordnen zu</Label>
            <Select
              value={block.manualEmployeeId}
              onValueChange={(val) => onUpdateBlock(blockIndex, { manualEmployeeId: val })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Mitarbeiter wählen..." />
              </SelectTrigger>
              <SelectContent>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.name} {emp.employee_number ? `(${emp.employee_number})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Salary Entries */}
      {block.salary_entries && block.salary_entries.length > 0 ? (
        <div>
          <Label className="text-muted-foreground mb-2 block">
            Gefundene Gehaltsdaten ({block.selectedEntries.size} ausgewählt)
          </Label>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Bezeichnung</TableHead>
                <TableHead className="text-right">Betrag</TableHead>
                <TableHead>Gültig ab</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {block.salary_entries.map((entry, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Checkbox
                      checked={block.selectedEntries.has(index)}
                      onCheckedChange={() => onToggleEntry(blockIndex, index)}
                    />
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{entry.description || "Festbezug"}</p>
                      {entry.notes && (
                        <p className="text-sm text-muted-foreground">{entry.notes}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-bold">
                    {formatCurrency(entry.amount)}
                  </TableCell>
                  <TableCell>
                    {entry.effective_month && entry.effective_year ? (
                      `${entry.effective_month}/${entry.effective_year}`
                    ) : entry.effective_year ? (
                      entry.effective_year
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="p-4 rounded-lg border bg-destructive/10 text-destructive">
          <AlertTriangle className="h-5 w-5 inline mr-2" />
          Keine Gehaltsdaten in diesem Abschnitt gefunden
        </div>
      )}

      {block.additional_info && (
        <div className="p-3 rounded-lg bg-muted">
          <Label className="text-muted-foreground">Zusätzliche Informationen</Label>
          <p className="text-sm mt-1">{block.additional_info}</p>
        </div>
      )}
    </div>
  );
}
